/**
 * Pure statistics for the A/B test significance calculator. No React, no DOM —
 * kept separate from the widget so the maths can be sanity-checked in isolation.
 *
 * - Two-proportion z-test with a pooled standard error (the textbook test for
 *   "did variant B convert at a different rate than control A").
 * - Two-sided p-value from the standard normal CDF.
 * - Confidence interval on the absolute difference, using the unpooled SE.
 * - Required sample size per variant for a baseline rate, relative minimum
 *   detectable effect, significance level and power.
 */

export type ConfidenceLevel = 90 | 95 | 99
export type Power = 80 | 90

/** Two-sided critical values z(1 − α/2) for the supported confidence levels. */
export const Z_TWO_SIDED: Record<ConfidenceLevel, number> = {
  90: 1.6448536269514722,
  95: 1.959963984540054,
  99: 2.5758293035489004,
}

/** One-sided z(power) for the supported power levels. */
export const Z_POWER: Record<Power, number> = {
  80: 0.8416212335729143,
  90: 1.2815515655446004,
}

/**
 * Complementary error function for x ≥ 0, Abramowitz & Stegun 7.1.26
 * (max absolute error 1.5e-7). Working in erfc rather than 1 − erf keeps small
 * tail probabilities from collapsing to zero through cancellation.
 */
function erfcNonNegative(x: number): number {
  const p = 0.3275911
  const a1 = 0.254829592
  const a2 = -0.284496736
  const a3 = 1.421413741
  const a4 = -1.453152027
  const a5 = 1.061405429
  const t = 1 / (1 + p * x)
  const poly = ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t
  return poly * Math.exp(-x * x)
}

/** Standard normal CDF Φ(x). */
export function normalCdf(x: number): number {
  const tail = 0.5 * erfcNonNegative(Math.abs(x) / Math.SQRT2)
  return x >= 0 ? 1 - tail : tail
}

/** Two-sided p-value for a z statistic: P(|Z| ≥ |z|). */
export function twoSidedPValue(z: number): number {
  return Math.min(1, erfcNonNegative(Math.abs(z) / Math.SQRT2))
}

export interface VariantInput {
  visitors: number
  conversions: number
}

export type SignificanceResult =
  | { ok: false; hint: string }
  | {
      ok: true
      rateA: number
      rateB: number
      /** Absolute difference, B − A, as a fraction. */
      diff: number
      /** (B − A) ÷ A; NaN when A converted nothing. */
      relativeLift: number
      /** NaN when the pooled SE is zero (nothing to test). */
      z: number
      pValue: number
      significant: boolean
      ciLow: number
      ciHigh: number
      /** True when any expected count under the null is below 5. */
      smallSample: boolean
    }

export function significanceTest(
  a: VariantInput,
  b: VariantInput,
  confidence: ConfidenceLevel,
): SignificanceResult {
  for (const [name, v] of [
    ["A", a],
    ["B", b],
  ] as const) {
    if (!Number.isFinite(v.visitors) || !Number.isFinite(v.conversions)) {
      return { ok: false, hint: `Enter visitors and conversions for variant ${name}.` }
    }
    if (v.visitors <= 0) {
      return { ok: false, hint: `Variant ${name} needs at least one visitor.` }
    }
    if (v.conversions < 0) {
      return { ok: false, hint: `Variant ${name} cannot have negative conversions.` }
    }
    if (!Number.isInteger(v.visitors) || !Number.isInteger(v.conversions)) {
      return { ok: false, hint: `Use whole numbers for variant ${name} — these are counts.` }
    }
    if (v.conversions > v.visitors) {
      return {
        ok: false,
        hint: `Variant ${name} has more conversions than visitors. If one visitor can convert more than once, count converting visitors instead — this test needs a yes/no outcome per visitor.`,
      }
    }
  }

  const rateA = a.conversions / a.visitors
  const rateB = b.conversions / b.visitors
  const diff = rateB - rateA
  const relativeLift = rateA > 0 ? diff / rateA : NaN

  const pooled = (a.conversions + b.conversions) / (a.visitors + b.visitors)
  const sePooled = Math.sqrt(pooled * (1 - pooled) * (1 / a.visitors + 1 / b.visitors))
  const z = sePooled > 0 ? diff / sePooled : NaN
  const pValue = sePooled > 0 ? twoSidedPValue(z) : 1
  const alpha = 1 - confidence / 100
  const significant = sePooled > 0 && pValue < alpha

  const seUnpooled = Math.sqrt(
    (rateA * (1 - rateA)) / a.visitors + (rateB * (1 - rateB)) / b.visitors,
  )
  const margin = Z_TWO_SIDED[confidence] * seUnpooled

  const expected = [
    a.visitors * pooled,
    a.visitors * (1 - pooled),
    b.visitors * pooled,
    b.visitors * (1 - pooled),
  ]
  const smallSample = expected.some((e) => e < 5)

  return {
    ok: true,
    rateA,
    rateB,
    diff,
    relativeLift,
    z,
    pValue,
    significant,
    ciLow: diff - margin,
    ciHigh: diff + margin,
    smallSample,
  }
}

/**
 * Visitors needed in EACH variant to detect a move from `baseline` to
 * `baseline × (1 + relativeMde)` with a two-sided test:
 *
 *   n = [ z(1−α/2)·√(2·p̄(1−p̄)) + z(power)·√(p1(1−p1) + p2(1−p2)) ]² ÷ (p2 − p1)²
 *
 * where p̄ = (p1 + p2) ÷ 2. Returns NaN when the inputs cannot form a test.
 */
export function sampleSizePerVariant(
  baseline: number,
  relativeMde: number,
  confidence: ConfidenceLevel,
  power: Power,
): number {
  const p1 = baseline
  const p2 = baseline * (1 + relativeMde)
  if (!(p1 > 0 && p1 < 1) || !(p2 > 0 && p2 < 1) || p1 === p2) return NaN
  const pBar = (p1 + p2) / 2
  const numerator =
    Z_TWO_SIDED[confidence] * Math.sqrt(2 * pBar * (1 - pBar)) +
    Z_POWER[power] * Math.sqrt(p1 * (1 - p1) + p2 * (1 - p2))
  return Math.ceil((numerator * numerator) / ((p2 - p1) * (p2 - p1)))
}
